// Import Swiper core and required modules
import Swiper from "swiper";
import {
	Navigation,
	Pagination,
	Autoplay,
	FreeMode,
	EffectFade,
} from "swiper/modules";

// Import GSAP and its plugins
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import ScrollToPlugin from "gsap/ScrollToPlugin";
import GSDevTools from "gsap/GSDevTools";
import CustomEase from "gsap/CustomEase";

// Import smooth scrolling functionality
import { lenis, smoothScrolling, destroyLenis } from "./misc.js";

// Register GSAP plugins
gsap.registerPlugin(CustomEase);
gsap.registerPlugin(GSDevTools);
gsap.registerPlugin(ScrollToPlugin);

function homepage() {
	console.log("homepage");
	// Only execute this function if we're on the homepage
	const isHomePage = document.querySelector(
		'[data-name="image_text_sections"]'
	);
	if (!isHomePage) return;

	// Prevent browser scroll restoration and force scroll to top immediately
	if ("scrollRestoration" in history) {
		history.scrollRestoration = "manual";
	}

	// Force scroll to top BEFORE any animations are initialized - multiple approaches for reliability
	window.scrollTo(0, 0);
	document.documentElement.scrollTop = 0;
	document.body.scrollTop = 0;

	// Also ensure this happens after any potential browser scroll restoration
	setTimeout(() => {
		window.scrollTo(0, 0);
		document.documentElement.scrollTop = 0;
		document.body.scrollTop = 0;
	}, 0);

	// Clear any existing ScrollTrigger instances to prevent duplicates on refresh
	ScrollTrigger.getAll().forEach((trigger) => trigger.kill());

	// Reset any transformations that might be cached
	gsap.set(
		"[data-name='image_text_sections'], [data-name='quote_carousel_block'], [data-name='pinned_home_bottom']",
		{
			clearProps: "all",
		}
	);

	// Uncomment for GSAP development tools
	//GSDevTools.create();

	// Handle smooth scrolling properly
	if (lenis) {
		// If lenis exists, just stop it and reset the scroll position
		lenis.scrollTo(0, { immediate: true });

		// Only stop lenis if we're on the homepage
		if (document.querySelector('[data-name="intro_section"]')) {
			lenis.stop();
		}
	} else {
		// Only initialize if it doesn't exist yet
		smoothScrolling();
	}

	// Declare swipers at the top level of the function
	let mobileTextSwiper;
	let studentsSwiper;

	// Get main sections container
	const imageTextSections = document.querySelector(
		'[data-name="image_text_sections"]'
	);
	const imageTextSectionsMobile = document.querySelector(
		'[data-name="image_text_sections_mobile"]'
	);
	if (!imageTextSections) return;

	// Fade in the body
	gsap.to(document.body, { opacity: 1, duration: 1, ease: "power1.out" });

	// Scroll to top using lenis for smooth scrolling
	lenis.scrollTo(0, { immediate: true });

	// Stop lenis scrolling during intro animation
	lenis.stop();

	// Get border elements for animation
	const borderleft = document.querySelector('[data-name="border-left"]');
	const borderright = document.querySelector('[data-name="border-right"]');
	const bordertop = document.querySelector('[data-name="border-top"]');
	const borderbottom = document.querySelector('[data-name="border-bottom"]');
	const menu = document.querySelector('[data-js="toggle-main-menu"]');
	const menubottom = document.querySelector('[data-name="bottom-nav-buttons"]');

	// Hide border and menu elements initially
	gsap.set(
		[borderleft, borderright, bordertop, borderbottom, menu, menubottom],
		{
			autoAlpha: 0,
		}
	);

	// Get intro section elements
	const introSection = document.querySelector('[data-name="intro_section"]');
	const intro_home_logo = document.querySelector(
		'[data-name="intro_home_logo"]'
	);
	const intro_video = document.querySelector("#intro_video");
	const intro_home_content = document.querySelector(
		'[data-name="intro_home_content"]'
	);
	gsap.set(intro_home_logo, { autoAlpha: 0 });

	// Create a timeline for intro section animation
	const introTimeline = gsap.timeline({ paused: true }); // Set timeline to paused initially

	// Get border elements for intro animation
	const borders = {
		left: document.querySelector('[data-name="border_left_home"]'),
		right: document.querySelector('[data-name="border_right_home"]'),
		top: document.querySelector('[data-name="border_top_home"]'),
		bottom: document.querySelector('[data-name="border_bottom_home"]'),
	};

	// Calculate distances from content box to screen edges for border animations
	function calculateDistances() {
		const contentBox = document.querySelector(
			'[data-name="intro_home_content"]'
		);
		const contentRect = contentBox.getBoundingClientRect();

		return {
			toTop: contentRect.top,
			toBottom: window.innerHeight - contentRect.bottom,
			toLeft: contentRect.left,
			toRight: window.innerWidth - contentRect.right,
		};
	}

	// Store original content position before any animations
	const originalContentPosition = (() => {
		const contentBox = document.querySelector(
			'[data-name="intro_home_content"]'
		);
		const contentRect = contentBox.getBoundingClientRect();

		return {
			toTop: contentRect.top,
			toBottom: window.innerHeight - contentRect.bottom,
			toLeft: contentRect.left,
			toRight: window.innerWidth - contentRect.right,
		};
	})();

	// Equalizer function to make all from_to_carousel_slider_item_title elements the same height
	function equalizeSliderItemTitles() {
		console.log("equalizeSliderItemTitles called");
		const titleElements = document.querySelectorAll(
			'[data-name="from_to_carousel_slider_item_title"]'
		);
		console.log("Found title elements:", titleElements.length);
		if (titleElements.length === 0) return;

		// Reset heights to auto to get natural heights
		titleElements.forEach((element) => {
			element.style.height = "auto";
		});

		// Find the maximum height
		let maxHeight = 0;
		titleElements.forEach((element) => {
			const height = element.offsetHeight;
			console.log("Element height:", height);
			if (height > maxHeight) {
				maxHeight = height;
			}
		});

		console.log("Max height:", maxHeight);

		// Apply the maximum height to all elements
		titleElements.forEach((element) => {
			element.style.height = maxHeight + "px";
		});
	}

	// Create a responsive padding variable based on screen width
	const responsivePadding = window.innerWidth >= 992 ? 54 : 40;

	// Build intro animation sequence
	introTimeline
		.to(introSection, { opacity: 1, duration: 0.5, ease: "none" })
		.to(
			intro_home_content,
			{ autoAlpha: 0, duration: 0.5, ease: "none" },
			"+=4"
		)
		.to(
			intro_home_content,
			{
				duration: 1,
				ease: CustomEase.create("custom", "M0,0 C0.856,0 0.095,1.004 1,1 "),
				paddingTop: 0,
				paddingBottom: 0,
				height: 0,
			},
			"-=.5"
		)
		.to(intro_home_logo, { autoAlpha: 1, duration: 1, ease: "none" }, "-=.9")

		.to(
			document.documentElement,
			{
				"--borderlefthome": `48%`,
				"--borderrighthome": `48%`,
				"--spacehome": `10px`,
				duration: 1.1,
				ease: CustomEase.create("custom", "M0,0 C0.856,0 0.095,1.004 1,1 "),
			},
			"-=1.1"
		)

		.add(() => {
			// Calculate distances for border animations
			const distances = originalContentPosition;

			// Animate borders to screen edges
			gsap.to(borders.top, {
				y: -distances.toTop,
				x: -distances.toLeft + responsivePadding,
				width: "100vw",
				height: window.innerWidth < 992 ? "10px" : "16px",
				duration: 1.5,
				ease: CustomEase.create("custom", "M0,0 C0.856,0 0.095,1.004 1,1 "),
			});

			gsap.to(borders.bottom, {
				y: function () {
					const borderRect = borders.bottom.getBoundingClientRect();
					const viewportHeight = window.visualViewport
						? window.visualViewport.height
						: window.innerHeight;
					const distanceToBottom = viewportHeight - borderRect.bottom;
					return distanceToBottom;
				},
				x: -distances.toLeft + responsivePadding,
				width: "100vw",
				height: window.innerWidth < 992 ? "10px" : "16px",
				duration: 1.5,
				ease: CustomEase.create("custom", "M0,0 C0.856,0 0.095,1.004 1,1 "),
			});

			gsap.to(borders.left, {
				x: -distances.toLeft + responsivePadding,
				y: -distances.toTop,
				height: function () {
					const viewportHeight = window.visualViewport
						? window.visualViewport.height
						: window.innerHeight;
					return `${viewportHeight}px`;
				},
				width: window.innerWidth < 992 ? "10px" : "16px",
				duration: 1.5,
				ease: CustomEase.create("custom", "M0,0 C0.856,0 0.095,1.004 1,1 "),
			});

			gsap.to(borders.right, {
				x: distances.toRight - responsivePadding,
				y: -distances.toTop,
				height: function () {
					const viewportHeight = window.visualViewport
						? window.visualViewport.height
						: window.innerHeight;
					return `${viewportHeight}px`;
				},
				width: window.innerWidth < 992 ? "10px" : "16px",
				duration: 1.5,
				ease: CustomEase.create("custom", "M0,0 C0.856,0 0.095,1.004 1,1 "),
			});
		}, "+=1")

		.to(
			document.documentElement,
			{
				"--borderlefthome": `20%`,
				"--borderrighthome": `80%`,
				"--spacehome": `20px`,
				duration: 1.8,
				ease: CustomEase.create("custom", "M0,0 C0.856,0 0.095,1.004 1,1 "),
			},
			"-=.2"
		)
		.to(
			[menu, menubottom],
			{
				autoAlpha: 1,
				duration: 1,
				ease: "power1.inOut",
			},
			"-=.2"
		)
		.to(
			[borders.left, borders.right, borders.top, borders.bottom],
			{
				autoAlpha: 0,
				duration: 0.2,
				ease: "none",
			},
			"-=1"
		)
		.to(
			[borderleft, borderright, bordertop, borderbottom],
			{
				autoAlpha: 1,
				duration: 0.2,
				ease: "none",
			},
			"-=1.08"
		)
		.to(
			intro_home_logo,
			{ autoAlpha: 0, duration: 0.5, ease: "power1.inOut" },
			"-=2.8"
		)
		.to(
			[
				intro_video,
				'[data-name="intro_video_poster"], [data-name="intro_video_overlay"]',
			],
			{ autoAlpha: 0, duration: 0.5, ease: "power1.inOut" },
			"-=1.8",
			() => {
				if (!document.querySelector("#intro_video")) return;
			}
		)
		.to(
			window,
			{
				duration: window.innerWidth > 992 ? 2 : 0.5,
				scrollTo: {
					y: window.innerWidth > 992 ? window.innerHeight * 1 : 0,
					autoKill: false,
				},
				ease: "power2.inOut",
				onComplete: () => {
					// Re-enable smooth scrolling after intro animation
					lenis.start();

					// Fade in mobile image text sections on smaller screens
					if (window.innerWidth < 992) {
						const mobileImageTextSections = document.querySelector(
							'[data-name="image_text_sections_mobile"]'
						);
						if (mobileImageTextSections) {
							gsap.to(mobileImageTextSections, {
								opacity: 1,
								duration: 1,
								ease: "power1.inOut",
								onComplete: () => {
									// Start autoplay after fade-in completes
									if (mobileTextSwiper) {
										// Make sure autoplay is enabled before starting it
										mobileTextSwiper.autoplay.enabled = true;
										mobileTextSwiper.autoplay.start();

										// Start the progress bar after autoplay starts
										const progressBar = mobileTextSwiper.progressBar;
										if (progressBar) {
											gsap.set(progressBar, { height: 0 });
										}

										// Animate images on the first slide
										const firstSlide =
											mobileTextSwiper.slides[mobileTextSwiper.activeIndex];
										if (firstSlide) {
											// Get the images in the first slide
											const img1 = firstSlide.querySelector(
												".img1-wrapper .grid__item-img"
											);
											const img2 = firstSlide.querySelector(
												".img2-wrapper .grid__item-img"
											);
											const img1Inner = img1?.querySelector(
												".grid__item-img-inner"
											);
											const img2Inner = img2?.querySelector(
												".grid__item-img-inner"
											);

											// No need to set initial scale again, already set in init

											// Animate first image with zoom effect
											gsap.to(img1, {
												scale: 1,
												duration: 0.6,
												ease: "power2.out",
												delay: 0.3,
											});

											if (img1Inner) {
												gsap.to(img1Inner, {
													scale: 1,
													duration: 0.6,
													ease: "power2.out",
													delay: 0.3,
												});
											}

											// Animate second image with a slight delay
											gsap.to(img2, {
												scale: 1,
												duration: 0.6,
												ease: "power2.out",
												delay: 0.7,
											});

											if (img2Inner) {
												gsap.to(img2Inner, {
													scale: 1,
													duration: 0.6,
													ease: "power2.out",
													delay: 0.7,
												});
											}
										}
									}
								},
							});
						}
						const quoteContent = document.querySelector(
							'[data-name="quote_section"]'
						);
						const quoteText = document.querySelector(
							'[data-name="quote_text"]'
						);
						const quoteLine = document.querySelector(
							'[data-name="quote_line"]'
						);
						const quoteTextSplit = new SplitText(quoteText, {
							type: "chars, words",
							linesClass: "char",
						});

						// Create the timeline with proper animations
						const splitTextMobileTL = gsap.timeline({
							scrollTrigger: {
								trigger: quoteContent,
								start: "top center",
								end: "center center", // Changed from "center center" to provide more scroll distance
								scrub: 1, // Changed from true to 1 for smoother scrubbing
								// markers: true,
								onEnter: () => {
									console.log("onEnter NOW lunch");
								},
								onEnterBack: () => {
									console.log("onEnterBack");
								},
							},
						});

						// Add the animation to the timeline properly
						splitTextMobileTL.fromTo(
							quoteTextSplit.chars,
							{
								opacity: 0.2,
							},
							{
								opacity: 1,
								stagger: 0.005,
								ease: "power1.out",
								duration: 0.04,
							}
						);

						// Remove the separate ScrollTrigger.create since we're using the timeline's built-in ScrollTrigger
					}

					// Prevent scrolling back up past the first section only on desktop
					if (lenis && window.innerWidth > 992) {
						// Modify lenis options to prevent scrolling above viewport height
						const originalCallback = lenis.onScroll;

						lenis.on("scroll", (e) => {
							if (e.scroll < window.innerHeight) {
								lenis.scrollTo(window.innerHeight, { immediate: true });
							}

							// Call the original callback if it exists
							if (originalCallback) originalCallback(e);
						});
					}
				},
			},
			"-=1"
		);

	const videoWrapper = document.querySelector(
		'[data-name="intro_video_wrapper"]'
	);

	if (!videoWrapper) {
		console.error("Video wrapper not found");
		return;
	}

	gsap.to(videoWrapper, {
		opacity: 1,
		duration: 1,
		delay: 0.1,
		ease: "power1.inOut",
		onComplete: () => {
			if (intro_video) {
				//alert('video')
				// Create a new Vimeo player with background mode
				const player = new Vimeo.Player(intro_video, {
					id: intro_video.getAttribute("src").match(/video\/(\d+)/)[1],
					background: true,
					autopause: false,
					autoplay: true,
					loop: true,
					muted: true,
					playsinline: true,
					responsive: true,
					dnt: true,
				});

				// Add event listeners for debugging
				player.on("loaded", () => {
					//alert('Video loaded');
					player.play();
				});

				player.on("error", (error) => {
					//alert('Vimeo player error:', error);
				});

				player.on("play", () => {
					//alert('Video started playing');
					// Fade in the video wrapper when video starts playing
				});

				// Handle initial load
				player
					.ready()
					.then(() => {
						//alert('Player ready');
						// Ensure video is muted and ready to play
						return Promise.all([
							player.setVolume(0),
							player.setLoop(true),
							player.setAutopause(false),
						]);
					})
					.then(() => {
						//alert('Player configured');
						// Start playing
						return player.play();
					})
					.catch((error) => {
						console.error("Error during player initialization:", error);
						// Start timeline anyway after a delay
					});
			} else {
				//alert('No intro video found');
				// If no video, start timeline immediately
				introTimeline.play();
			}
			// Start the intro timeline after video is visible
			introTimeline.play();
		},
	});

	// Add Vimeo poster functionality
	const videoFrame = document.getElementById("intro_video");
	if (videoFrame) {
		const player = new Vimeo.Player(videoFrame);
		const videoId = videoFrame.getAttribute("data-vimeo-id");

		// Get the video thumbnail using the video ID
		fetch(`https://vimeo.com/api/v2/video/${videoId}.json`)
			.then((response) => response.json())
			.then((data) => {
				const thumbnailUrl = data[0].thumbnail_large;

				// Create a poster image element
				const poster = document.createElement("div");
				poster.className =
					"absolute inset-0 w-screen h-[56.25vw] min-h-screen min-w-[177.77vh] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-cover bg-center bg-no-repeat";
				poster.style.backgroundImage = `url(${thumbnailUrl})`;

				// Insert the poster before the iframe
				videoFrame.parentNode.insertBefore(poster, videoFrame);

				// Hide the poster when the video starts playing
				player.on("play", function () {
					poster.style.opacity = "0";
					poster.style.transition = "opacity 0.5s ease-out";
				});
			})
			.catch(function (error) {
				console.error("Error getting Vimeo thumbnail:", error);
			});
	}

	// Get grid items for section animations
	const gridItems = imageTextSections.querySelectorAll(
		'[data-name="grid_banner_home"]'
	);
	const quoteSection = document.querySelector('[data-name="quote_section"]');
	const quoteCarouselBlock = document.querySelector(
		'[data-name="quote_carousel_block"]'
	);

	// Register ScrollTrigger and SplitText plugins
	gsap.registerPlugin(ScrollTrigger);
	gsap.registerPlugin(SplitText);

	// Initialize mobile slider for image text sections on screens smaller than 992px
	const mobileImageTextSlider = document.querySelector(
		'[data-target="image_text_mobile_slider"]'
	);

	if (mobileImageTextSlider) {
		// Function to update content height for each slide
		function updateContentHeight() {
			const slides = document.querySelectorAll(
				'[data-name="image_text_sections_mobile"] .swiper-slide'
			);
			slides.forEach((slide) => {
				const contentContainer = slide.querySelector(
					'[data-name="content_mobile_container"]'
				);
				const imgCalculate = slide.querySelector(
					'[data-name="img_mobile_calculate"]'
				);
				if (contentContainer && imgCalculate) {
					const contentHeight = contentContainer.offsetHeight;
					const screenHeight = window.innerHeight;
					imgCalculate.style.height = `calc(${screenHeight}px - 220px - ${contentHeight}px)`;
				}
			});
		}

		// Initialize the swiper with autoplay initially disabled
		mobileTextSwiper = new Swiper(mobileImageTextSlider, {
			modules: [Pagination, Navigation, Autoplay, EffectFade],
			slidesPerView: 1,
			spaceBetween: 0,
			effect: "fade",
			fadeEffect: {
				crossFade: true,
			},
			speed: 800,
			loop: true,
			autoHeight: false,
			autoplay: {
				delay: 5000,
				disableOnInteraction: false,
				enabled: false, // Start with autoplay disabled
			},
			pagination: {
				el: '[data-target="image_text_mobile_slider"] .swiper-pagination',
				clickable: true,
				type: "fraction",
			},
			navigation: {
				nextEl: ".mobile-nav-next",
				prevEl: ".mobile-nav-prev",
			},
			on: {
				init: function () {
					// Update content height on init
					updateContentHeight();

					// Ensure all slides have equal height and set initial opacity
					const slides = this.slides;
					const activeIndex = this.activeIndex;

					slides.forEach((slide, index) => {
						slide.style.height = "100%";
						// Make only the active slide visible initially
						slide.style.opacity = index === activeIndex ? "1" : "0";
						slide.style.transition = "opacity 800ms ease";

						// Get image elements
						const img1 = slide.querySelector(".img1-wrapper .grid__item-img");
						const img2 = slide.querySelector(".img2-wrapper .grid__item-img");
						const img1Inner = img1?.querySelector(".grid__item-img-inner");
						const img2Inner = img2?.querySelector(".grid__item-img-inner");

						if (img1 && img2) {
							// For active slide, prep for animation
							if (index === activeIndex) {
								// Set initial scale for active slide (will be animated after fade-in)
								gsap.set([img1, img2], {
									scale: 0,
									transformOrigin: "100% 0%",
								});

								if (img1Inner && img2Inner) {
									gsap.set([img1Inner, img2Inner], {
										scale: 1.5,
										transformOrigin: "100% 0%",
									});
								}
							} else {
								// For non-active slides, just set to scale 0
								gsap.set([img1, img2], {
									scale: 0,
									transformOrigin: "100% 0%",
								});

								if (img1Inner && img2Inner) {
									gsap.set([img1Inner, img2Inner], {
										scale: 1.5,
										transformOrigin: "100% 0%",
									});
								}
							}
						}
					});

					// Initialize slide counter
					updateSlideCounter(this);

					// Set up progress bar elements
					const progressBar =
						mobileImageTextSlider.querySelector(".slide-progress");
					if (progressBar) {
						this.progressBar = progressBar;
					}

					// Set up Read More/Less buttons for mobile content
					const readMoreButtons = document.querySelectorAll(
						'[data-name="read_more_button"]'
					);
					const readLessButtons = document.querySelectorAll(
						'[data-name="read_less_button"]'
					);

					readMoreButtons.forEach((button) => {
						button.addEventListener("click", function (e) {
							e.preventDefault();

							// Pause the swiper autoplay
							mobileTextSwiper.autoplay.stop();

							// Get the pagination and navigation elements
							const navPrev = button
								.closest(".swiper")
								.querySelector(".mobile-nav-prev");
							const navNext = button
								.closest(".swiper")
								.querySelector(".mobile-nav-next");
							const slideCounter = button
								.closest(".swiper")
								.querySelector(".slide-counter");
							const slideProgress = button
								.closest(".swiper")
								.querySelector(".slide-progress");

							// Hide pagination and navigation
							if (navPrev) gsap.to(navPrev, { opacity: 0, duration: 0.3 });
							if (navNext) gsap.to(navNext, { opacity: 0, duration: 0.3 });
							if (slideCounter)
								gsap.to(slideCounter, { opacity: 0, duration: 0.3 });
							if (slideProgress)
								gsap.to(slideProgress.parentElement, {
									opacity: 0,
									duration: 0.3,
								});

							// Animate the content into view
							const content = button
								.closest(".swiper-slide")
								.querySelector('[data-name="content_mobile"]');
							if (content) {
								gsap.to(content, {
									y: 0,
									duration: 0.5,
									ease: "power2.out",
								});
							}
						});
					});

					readLessButtons.forEach((button) => {
						button.addEventListener("click", function (e) {
							e.preventDefault();

							// Get the pagination and navigation elements
							const swiperContainer = button.closest(".swiper");
							const navPrev = swiperContainer.querySelector(".mobile-nav-prev");
							const navNext = swiperContainer.querySelector(".mobile-nav-next");
							const slideCounter =
								swiperContainer.querySelector(".slide-counter");
							const slideProgress =
								swiperContainer.querySelector(".slide-progress");

							// Animate the content back down
							const content = button.closest('[data-name="content_mobile"]');
							if (content) {
								gsap.to(content, {
									y: "100%",
									duration: 0.5,
									ease: "power2.in",
									onComplete: function () {
										// Resume the swiper autoplay after animation completes
										mobileTextSwiper.autoplay.start();

										// Show pagination and navigation again
										if (navPrev)
											gsap.to(navPrev, { opacity: 1, duration: 0.3 });
										if (navNext)
											gsap.to(navNext, { opacity: 1, duration: 0.3 });
										if (slideCounter)
											gsap.to(slideCounter, { opacity: 1, duration: 0.3 });
										if (slideProgress)
											gsap.to(slideProgress.parentElement, {
												opacity: 1,
												duration: 0.3,
											});
									},
								});
							}
						});
					});
				},
				slideChangeTransitionStart: function () {
					// Handle fade effect manually
					this.slides.forEach((slide, index) => {
						if (index === this.activeIndex) {
							// Fade in the active slide
							slide.style.opacity = "1";

							// Get the images in the current slide with correct selectors
							const img1Wrapper = slide.querySelector(".img1-wrapper");
							const img2Wrapper = slide.querySelector(".img2-wrapper");
							const img1 = img1Wrapper?.querySelector(".grid__item-img");
							const img2 = img2Wrapper?.querySelector(".grid__item-img");
							const img1Inner = img1?.querySelector(".grid__item-img-inner");
							const img2Inner = img2?.querySelector(".grid__item-img-inner");

							// Only proceed with animation if elements exist
							if (img1 && img2) {
								// Reset initial state
								gsap.set([img1, img2], {
									scale: 0,
									transformOrigin: "100% 0%",
								});

								if (img1Inner && img2Inner) {
									gsap.set([img1Inner, img2Inner], {
										scale: 1.5,
										transformOrigin: "100% 0%",
									});
								}

								// Animate first image with zoom effect
								gsap.to(img1, {
									scale: 1,
									duration: 0.6,
									ease: "power2.out",
									delay: 0.3, // Delay to allow fade transition to complete first
								});

								if (img1Inner) {
									gsap.to(img1Inner, {
										scale: 1,
										duration: 0.6,
										ease: "power2.out",
										delay: 0.3,
									});
								}

								// Animate second image with a slight delay
								gsap.to(img2, {
									scale: 1,
									duration: 0.6,
									ease: "power2.out",
									delay: 0.7, // Slightly delayed compared to first image
								});

								if (img2Inner) {
									gsap.to(img2Inner, {
										scale: 1,
										duration: 0.6,
										ease: "power2.out",
										delay: 0.7,
									});
								}
							}
						} else {
							// Fade out all other slides
							slide.style.opacity = "0";
						}
					});

					// Update counter on slide change
					updateSlideCounter(this);

					// Reset progress bar
					if (this.progressBar) {
						gsap.set(this.progressBar, { height: 0 });
					}
				},
				autoplayTimeLeft: function (swiper, timeLeft, percentage) {
					// Update progress bar based on autoplay time left
					if (swiper.progressBar) {
						// Convert percentage to height percentage
						const progressHeight = (1 - percentage) * 100;
						gsap.to(swiper.progressBar, {
							height: `${progressHeight}%`,
							duration: 0.1, // Small duration for smooth updates
							ease: "none",
						});
					}
				},
			},
		});

		// Function to update the slide counter
		function updateSlideCounter(swiper) {
			const counter = mobileImageTextSlider.querySelector(".slide-counter");
			if (counter) {
				const currentIndex = swiper.realIndex + 1;
				const totalSlides = swiper.slides.length;
				counter.textContent = `${currentIndex} of ${totalSlides}`;
			}
		}
	}

	// Apply ScrollTrigger only for desktop
	if (imageTextSections && window.innerWidth >= 992) {
		// Pin the entire image_text_sections for the duration of all grid sections
		ScrollTrigger.create({
			trigger: imageTextSections,
			start: "top top",
			end: `+=${gridItems.length * window.innerHeight * 2}px`,
			pin: true,
			pinSpacing: true,
			scrub: 1,
		});

		// Set initial state for all grid sections (hidden)
		gsap.set(gridItems, { autoAlpha: 0 });

		// Create a master timeline for all grid items
		const masterTimeline = gsap.timeline({
			scrollTrigger: {
				trigger: imageTextSections,
				start: "top top",
				end: `+=${gridItems.length * window.innerHeight * 2}px`,
				scrub: 1,
				//markers: true,
			},
		});

		// Build animations for each grid section
		gridItems.forEach((item, index) => {
			const copyElements = item.querySelectorAll(".grid_item_copy");
			const images = item.querySelectorAll(".grid__item-img");
			const imageInners = item.querySelectorAll(".grid__item-img-inner");

			const startPosition = index * 0.4; // Reduced from 0.7 to 0.4 for tighter spacing

			// Show the section
			masterTimeline.set(item, { autoAlpha: 1 }, startPosition);

			// Animate in text elements
			masterTimeline.fromTo(
				copyElements,
				{ opacity: 0 },
				{
					opacity: 1,
					duration: 0.15,
					stagger: 0.04,
				},
				startPosition
			);

			masterTimeline.fromTo(
				'[data-target="banner_arrow_home"]',
				{ scale: 0 },
				{ scale: 1, duration: 0.1 },
				startPosition + 0.1
			);

			// Animate in first image with scale effect
			masterTimeline.fromTo(
				images[0],
				{
					scale: 0,
					transformOrigin: images[0].classList.contains("origin-top-right")
						? "100% 0%"
						: "0% 0%",
				},
				{
					scale: 1,
					duration: 0.075,
				},
				startPosition
			);

			// Animate inner image content (zoom effect)
			masterTimeline.fromTo(
				imageInners[0],
				{
					scale: 3,
					transformOrigin: images[0].classList.contains("origin-top-right")
						? "100% 0%"
						: "0% 0%",
				},
				{
					scale: 1,
					duration: 0.075,
				},
				startPosition
			);

			// Animate in second image with delay
			masterTimeline.fromTo(
				images[1],
				{
					scale: 0,
					transformOrigin: images[0].classList.contains("origin-top-right")
						? "100% 0%"
						: "0% 0%",
				},
				{
					scale: 1,
					duration: 0.075,
				},
				startPosition + 0.075
			);

			// Animate inner content of second image
			masterTimeline.fromTo(
				imageInners[1],
				{
					scale: 3,
					transformOrigin: images[0].classList.contains("origin-top-right")
						? "100% 0%"
						: "0% 0%",
				},
				{
					scale: 1,
					duration: 0.075,
				},
				startPosition + 0.075
			);

			// Hold state duration before exit animations
			const holdDuration = 0.1;

			// Calculate when exit animations should start - reduced from 0.35 to 0.25
			const exitStart = startPosition + 0.25;

			// Animate out first image
			masterTimeline.to(
				images[0],
				{
					scale: 0,
					duration: 0.075,
					ease: "power1.in",
					transformOrigin: images[0].classList.contains("origin-top-right")
						? "0% 100%"
						: "100% 100%",
				},
				exitStart
			);

			// Animate out first image
			masterTimeline.to(
				'[data-target="banner_arrow_home"]',
				{
					scale: 0,
					duration: 0.075,
					ease: "power1.in",
				},
				exitStart
			);

			// Zoom effect for first image exit
			masterTimeline.to(
				imageInners[0],
				{
					scale: 3,
					duration: 0.075,
					ease: "power1.in",
					transformOrigin: images[0].classList.contains("origin-top-right")
						? "0% 100%"
						: "100% 100%",
				},
				exitStart
			);

			// Animate out second image with delay
			masterTimeline.to(
				images[1],
				{
					scale: 0,
					duration: 0.075,
					ease: "power1.in",
					transformOrigin: images[1].classList.contains("origin-top-right")
						? "0% 100%"
						: "100% 100%",
				},
				exitStart + 0.075
			);

			// Zoom effect for second image exit
			masterTimeline.to(
				imageInners[1],
				{
					scale: 3,
					duration: 0.075,
					ease: "power1.in",
					transformOrigin: images[1].classList.contains("origin-top-right")
						? "0% 100%"
						: "100% 100%",
				},
				exitStart + 0.075
			);

			// Fade out text elements
			masterTimeline.to(
				copyElements,
				{
					opacity: 0,
					duration: 0.15,
					stagger: 0.02,
					ease: "power1.in",
				},
				exitStart
			);

			// Hide section completely before next section starts
			masterTimeline.set(item, { autoAlpha: 0 }, exitStart + 0.15);

			// Start next section immediately after this one ends
			if (index < gridItems.length - 1) {
				const nextItem = gridItems[index + 1];
				masterTimeline.set(nextItem, { autoAlpha: 1 }, exitStart + 0.15);
			}
		});
	}

	// Prevent scrolling back to intro section - only for screens larger than 992px
	window.addEventListener(
		"wheel",
		function (e) {
			if (
				window.innerWidth > 992 &&
				window.scrollY <= window.innerHeight &&
				e.deltaY < 0
			) {
				e.preventDefault();
				if (lenis) {
					lenis.scrollTo(window.innerHeight, { immediate: true });
				} else {
					window.scrollTo({
						top: window.innerHeight,
						behavior: "auto",
					});
				}
				return false;
			}
		},
		{ passive: false }
	);

	// Get quote section elements
	const quoteText = document.querySelector('[data-name="quote_text"]');
	if (!quoteText) return;

	// Get the quote line element
	const quoteLine = document.querySelector('[data-name="quote_line"]');

	// Split text for character-by-character animation
	const quoteTextSplit = new SplitText(quoteText, {
		type: "chars, words",
		linesClass: "char",
	});

	// Check if we're using version 2 (scroll-based) or original carousel
	const isVersion2 = document.querySelector(
		'[data-name="carousel_section"][data-version="2"]'
	);

	// Create timeline for quote section - different behavior for mobile vs desktop
	let splitTextTL;

	// Get target color from data attribute
	const quoteCarouselBg2 = document.querySelector(
		'[data-name="quote_carousel_bg_2"]'
	);
	const targetColor = quoteCarouselBg2
		? quoteCarouselBg2.getAttribute("data-color") || "#A0202C"
		: "#A0202C";

	// Check if we're on mobile or desktop
	if (window.innerWidth < 992) {
		// Mobile version - simpler animation without horizontal sliding
		// splitTextTL = gsap.timeline({
		// 	scrollTrigger: {
		// 		trigger: quoteCarouselBlock,
		// 		start: "top 50%",
		// 		end: "+=300px",
		// 		scrub: true,
		// 		onEnter: function () {
		// 			if (quoteSection) {
		// 				gsap.to(quoteSection, {
		// 					opacity: 1,
		// 					duration: 0.5,
		// 					ease: "power4.out",
		// 				});
		// 			}

		// 			setTimeout(() => {
		// 				startCarouselIfReady();
		// 			}, 800);
		// 		},
		// 		onLeaveBack: function () {
		// 			if (quoteSection) {
		// 				gsap.to(quoteSection, {
		// 					opacity: 0,
		// 					duration: 0.3,
		// 					ease: "power1.inOut",
		// 				});
		// 			}
		// 		},
		// 	},
		// });

		// Create a separate ScrollTrigger for the background color change on mobile
		// This will trigger when the quote content comes into view
		const quoteContent = document.querySelector('[data-name="quote_section"]');
		if (quoteContent && quoteCarouselBg2) {
			ScrollTrigger.create({
				trigger: quoteContent,
				start: "top center",
				end: "center center",
				markers: false,
				onEnter: function () {
					// Only add bg-red if we're not on initial page load or if elements aren't already visible
					const isInitialLoad = window.scrollY === 0;
					// const elementsInView = quoteContent.getBoundingClientRect().top <= 0;
					gsap.to('[data-name="image_text_sections_mobile"]', {
						backgroundColor: targetColor,
						duration: 1.5,
						ease: "power1.inOut",
					});
					gsap.to(quoteCarouselBg2, {
						backgroundColor: targetColor,
						duration: 1.5,
						ease: "power1.inOut",
						onComplete: function () {
							// Only add bg-red class if not initial load or if elements are coming into view
							if (!isInitialLoad) {
								const sliderItems = document.querySelectorAll(
									'[data-name="from_to_carousel_slider_item"]'
								);
								sliderItems.forEach((item) => {
									item.classList.add("bg-red");
								});
							}
						},
					});
				},
				onLeaveBack: function () {
					gsap.to('[data-name="image_text_sections_mobile"]', {
						backgroundColor: "transparent", // or whatever the original color should be
						duration: 0.8,
						ease: "power1.inOut",
					});
					gsap.to(quoteCarouselBg2, {
						backgroundColor: "transparent", // or whatever the original color should be
						duration: 0.8,
						ease: "power1.inOut",
					});

					// Remove bg-red class from all from_to_carousel_slider_item elements
					// const sliderItems = document.querySelectorAll(
					// 	'[data-name="from_to_carousel_slider_item"]'
					// );
					// sliderItems.forEach((item) => {
					// 	item.classList.remove("bg-red");
					// });
				},
			});
		}

		// Add global event listener to resume autoplay when video modal closes
		document.addEventListener("click", function (e) {
			if (e.target.closest(".mfp-close") || e.target.closest(".mfp-bg")) {
				// Resume autoplay when video modal is closed
				if (
					mobileFromToSwiper &&
					mobileFromToSwiper.autoplay &&
					window.innerWidth < 992
				) {
					mobileFromToSwiper.autoplay.start();
				}
			}
		});

		const fromTocarosuel = document.querySelector(
			'[data-name="from_to_carousel_slider"]'
		);

		const mobileFromToSwiper = new Swiper(fromTocarosuel, {
			modules: [Navigation, Pagination, EffectFade, Autoplay],
			slidesPerView: 1,
			spaceBetween: 0,
			speed: 300, // Reduced from 500ms to 300ms for faster transitions
			effect: "fade",
			loop: true,
			autoHeight: true,
			autoplay: {
				delay: 5000,
				disableOnInteraction: false,
			},
			pagination: {
				el: '[data-name="from_to_carousel_slider"] .swiper-pagination',
				clickable: true,
				type: "bullets",
				renderBullet: function (index, className) {
					return (
						'<span class="' +
						className +
						'">' +
						"<i></i>" +
						"<b></b>" +
						"</span>"
					);
				},
			},
			on: {
				autoplayPause: function () {
					console.info("david autoplayPause");
				},
				autoplayPlay: function () {
					console.info("david autoplayPlay");
				},
				autoplayResume: function () {
					console.info("david autoplayResume");
				},
				slideChangeTransitionStart: function () {
					console.info("david slideChange");
					const currentSlide =
						mobileFromToSwiper.slides[mobileFromToSwiper.activeIndex];
					const previousSlide =
						mobileFromToSwiper.slides[mobileFromToSwiper.previousIndex];

					const previousIllustrationOverlay = previousSlide.querySelector(
						'[data-name="illustration_overlay"]'
					);

					const previousToTitle = previousSlide.querySelector(
						'[data-name="to_title"]'
					);
					const previousVideoCta = previousSlide.querySelector(
						'[data-name="video_cta"]'
					);

					gsap.set(previousIllustrationOverlay, { opacity: 1 });
					gsap.to([previousToTitle, previousVideoCta], {
						duration: 0.3,
						opacity: 0,
					});

					const toTitle = currentSlide.querySelector('[data-name="to_title"]');

					const illustration = currentSlide.querySelector(".illustration");
					const illustrationOverlay = currentSlide.querySelector(
						'[data-name="illustration_overlay"]'
					);
					const videoCta = currentSlide.querySelector(
						'[data-name="video_cta"]'
					);

					const timeline = gsap.timeline();

					// Fade in illustration with scale animation (from 0.9 to 1.0)
					timeline.to(null, { duration: 2 });
					gsap.set(illustration, { opacity: 1, scale: 1.0 });

					timeline.to(
						illustrationOverlay,
						{
							opacity: 0,
							duration: 0.6,
							ease: "linear",
						},
						"remove-overlay"
					);
					timeline.to(
						[toTitle, videoCta],
						{
							opacity: 1,
							duration: 0.6,
							ease: "linear",
						},
						"remove-overlay"
					);

					timeline.to(
						illustration,

						{
							opacity: 0,
							scale: 1.1,
							duration: 0.6,
							ease: "power2.out",
						},
						"remove-overlay"
					);
				},
			},
		});

		mobileFromToSwiper.autoplay.stop();

		gsap.set(["[data-name='to_title']", "[data-name='video_cta']"], {
			opacity: 0,
		});

		gsap.set("[data-name='illustration_overlay']", { opacity: 1 });

		// Add a flag to track if the onEnter has already been executed
		let hasEntered = false;

		ScrollTrigger.create({
			trigger: fromTocarosuel,
			start: "top 75%",
			end: "+=300px",
			// markers: true,
			scrub: false,
			onEnter: function () {
				// Only execute once
				if (hasEntered) return;
				hasEntered = true;
				console.info("swiper start scroll trigger");
				console.info("david onEnter");
				mobileFromToSwiper.slideTo(1, 0, false);

				mobileFromToSwiper.slideTo(0, 0, false);
				gsap.to('[data-name="from_to_carousel_slider"] .swiper-wrapper', {
					opacity: 1,
					duration: 0.5,
					delay: 0.4,
					ease: "linear",
				});

				const firstSlide = document.querySelector(
					"[data-name='from_to_carousel_slider_mobile'] .swiper-slide:nth-child(1)"
				);
				gsap.set(firstSlide.querySelector(".illustration"), {
					opacity: 1,
				});

				const timeline = gsap.timeline();

				// Fade in illustration with scale animation (from 0.9 to 1.0)
				timeline.to(null, { duration: 2 });

				timeline.to(
					firstSlide.querySelector("[data-name='illustration_overlay']"),
					{
						opacity: 0,
						duration: 0.6,
						ease: "linear",
					},
					"remove-overlay"
				);
				timeline.to(
					firstSlide.querySelectorAll(
						"[data-name='to_title'], [data-name='video_cta']"
					),
					{
						opacity: 1,
						duration: 0.6,
						ease: "linear",
					},
					"remove-overlay"
				);

				timeline.to(
					firstSlide.querySelector(".illustration"),

					{
						opacity: 0,
						scale: 1.1,
						duration: 0.6,
						ease: "power2.out",
					},
					"remove-overlay"
				);

				mobileFromToSwiper.autoplay.start();
			},
		});
	} else {
		// Desktop version - with horizontal sliding and more complex animation
		splitTextTL = gsap.timeline({
			scrollTrigger: {
				trigger: quoteCarouselBlock,
				start: "top 0%",
				end: isVersion2 ? "+=600%" : "+=300%", // 4x screen height for version 2, 3x for original
				scrub: true,
				pin: true,
				anticipatePin: 1,
				pinSpacing: true,
				onEnter: function () {
					if (quoteSection) {
						gsap.to(quoteSection, {
							opacity: 1,
							duration: 0.5,
							ease: "power4.out",
						});
					}
				},
				onLeave: function () {
					if (quoteSection) {
						gsap.to(quoteSection, {
							opacity: 0,
							duration: 0.3,
							ease: "power1.inOut",
						});
					}
				},
				onEnterBack: function () {
					if (quoteSection) {
						gsap.to(quoteSection, {
							opacity: 1,
							duration: 0.5,
							ease: "power4.out",
						});
					}
					document.querySelector(
						'[data-name="quote_carousel_block"]'
					).style.zIndex = "5";
					const pinnedSection = document
						.querySelector('[data-name="quote_carousel_block"]')
						.closest(".pin-spacer");
					if (pinnedSection) {
						pinnedSection.style.zIndex = "5";
					}
				},
				onLeaveBack: function () {
					if (quoteSection) {
						gsap.to(quoteSection, {
							opacity: 0,
							duration: 0.3,
							ease: "power1.inOut",
						});
					}
					document.querySelector(
						'[data-name="quote_carousel_block"]'
					).style.zIndex = "5";
					const pinnedSection = document
						.querySelector('[data-name="quote_carousel_block"]')
						.closest(".pin-spacer");
					if (pinnedSection) {
						pinnedSection.style.zIndex = "5";
					}
				},
			},
		});

		// Initial animation sequence (same for both versions)
		if (window.innerWidth > 992) {
			splitTextTL.from(
				quoteTextSplit.chars,
				{
					opacity: 0.2,
					stagger: 0.005,
					ease: "power1.out",
					duration: 0.04,
				},
				0
			);
		}
		splitTextTL
			.to(
				'[data-name="quote_carousel_bg"]',
				{
					opacity: 1,
					duration: 0.1,
				},
				0
			)
			.to(quoteCarouselBlock, {
				xPercent: -50,
				ease: "none",
				duration: 1,
				onStart: function () {
					// Start carousel when horizontal scroll begins
					if (typeof window.initHomeCarousel === "function") {
						setTimeout(() => {
							window.initHomeCarousel();
						}, 500);
					}
				},
				onComplete: function () {
					// Add illustration-bg class to all elements with data-name="illustration_bg" only in version 2
					if (isVersion2) {
						document
							.querySelectorAll('[data-name="illustration_bg"]')
							.forEach((element) => {
								element.classList.add("illustration-bg");
							});
					}
				},
			})
			.to(
				'[data-name="quote_carousel_bg_2"]',
				{
					opacity: 1,
					duration: 1,
					backgroundColor: targetColor,
				},
				"-=1"
			)
			.from(
				quoteLine,
				{
					width: 0,
					duration: 1,
					ease: "none",
				},
				"-=1"
			)
			.to(
				'[data-name="from_title"]',
				{
					opacity: 1,
					duration: 0.5,
					ease: "power2.out",
				},
				"-=0.7"
			) // Start opacity animation when quote line is 90% complete
			.to(
				".scroll-slide:first-child .illustration",
				{
					autoAlpha: 1,
					duration: 0.5,
					ease: "power2.out",
				},
				"-=0.7"
			); // Start illustration fade-in at the same time as from_title

		const slides = document.querySelectorAll(".scroll-slide");
		slides.forEach((slide, index) => {
			const illustration = slide.querySelector(".illustration");
			const illustrationImg = slide.querySelector(
				'.illustration img, .illustration .twk-image, .illustration [class*="w-full h-full object-cover"]'
			);
			const content = slide.querySelector(
				'[data-name="carousel_item_content"]'
			);
			const fromTitle = slide.querySelector('[data-name="from_title"]');
			const videoCta = slide.querySelector('[data-name="video_cta"]');
			const videoCtaSpan = videoCta.querySelector("span");

			// Set initial states
			gsap.set(content, { autoAlpha: 0 });
			gsap.set(illustration, { autoAlpha: index === 0 ? 0 : 0 }); // First illustration starts at opacity 0
			gsap.set(illustrationImg, { scale: index === 0 ? 1.0 : 0.9 }); // First slide starts at scale 1.0, others at 0.9
			gsap.set(fromTitle, { autoAlpha: index === 0 ? 0 : 0 }); // First from_title starts at opacity 0

			// Add animations to the timeline
			if (index === 0) {
				// First slide - no scale animation, only fade-in handled in initial timeline
				// No additional animations needed here
			} else {
				// Fade out previous slide with scale animation on image
				splitTextTL.to(slides[index - 1].querySelector(".illustration"), {
					autoAlpha: 0,
					duration: 0.5,
				});
				splitTextTL.to(
					slides[index - 1].querySelector(
						'.illustration img, .illustration .twk-image, .illustration [class*="w-full h-full object-cover"]'
					),
					{
						scale: 1.1,
						duration: 0.5,
					},
					"-=0.5"
				);
				splitTextTL.to(
					slides[index - 1].querySelector(
						'[data-name="carousel_item_content"]'
					),
					{
						autoAlpha: 0,
						duration: 0.5,
					},
					"-=0.5"
				);
				splitTextTL.to(
					slides[index - 1].querySelector('[data-name="from_title"]'),
					{
						autoAlpha: 0,
						duration: 0.5,
					},
					"-=0.5"
				);
			}

			// Fade in current slide illustration and from_title with scale animation on image (skip first slide)
			if (index !== 0) {
				splitTextTL.to(illustration, {
					autoAlpha: 1,
					duration: 0.5,
				});
				splitTextTL.to(
					illustrationImg,
					{
						scale: 1.0,
						duration: 0.5,
					},
					"-=0.5"
				);
				splitTextTL.to(
					fromTitle,
					{
						autoAlpha: 1,
						duration: 0.5,
					},
					"-=0.5"
				);
			}

			// Hold for a moment
			splitTextTL.to({}, { duration: 0.1 });

			// Fade out illustration with scale animation on image and fade in content
			splitTextTL.to(illustration, {
				autoAlpha: 0,
				duration: 0.75,
			});
			splitTextTL.to(
				illustrationImg,
				{
					scale: 1.1,
					duration: 0.75,
				},
				"-=0.75"
			);
			splitTextTL.to(
				content,
				{
					autoAlpha: 1,
					duration: 0.5,
				},
				"-=0.5"
			);

			splitTextTL.to(
				fromTitle,
				{
					autoAlpha: 0,
					duration: 0.5,
				},
				"-=0.5"
			);
			splitTextTL.from(
				videoCta,
				{
					scale: 0,
					duration: 0.5,
				},
				"-=0.5"
			);
			splitTextTL.from(
				videoCtaSpan,
				{
					autoAlpha: 0,
					duration: 0.3,
				},
				"-=0.1"
			);

			// Hold content visible
			splitTextTL.to({}, { duration: 0.5 });
		});

		// Final animation to scale up and blur
		splitTextTL
			.to(document.querySelector('[data-name="carousel_section"]'), {
				scale: 1.5,
				filter: "blur(10px)",
				transformOrigin: "center center",
				duration: 0.5,
			})
			.to(
				quoteCarouselBlock,
				{
					opacity: 0,
					duration: 0.5,
					zIndex: 0,
					onComplete: () => {
						document.querySelector(
							'[data-name="quote_carousel_block"]'
						).style.zIndex = "0";
						const pinnedSection = document
							.querySelector('[data-name="quote_carousel_block"]')
							.closest(".pin-spacer");
						if (pinnedSection) {
							pinnedSection.style.zIndex = "0";
						}
					},
				},
				"-=0.2"
			);
	}

	// Function to safely start the carousel
	function startCarouselIfReady() {
		const carouselSlider = document.querySelector(
			'[data-target="carousel_slider"]'
		);
		if (carouselSlider && typeof window.initHomeCarousel === "function") {
			window.initHomeCarousel();
		} else {
			// Try again after a shorter delay
			setTimeout(() => {
				if (carouselSlider && typeof window.initHomeCarousel === "function") {
					window.initHomeCarousel();
				}
			}, 100); // Reduced timeout from 500ms to 100ms
		}
	}

	// Global function to start the carousel when section becomes visible
	window.initHomeCarousel = function () {
		const carouselSlider = document.querySelector(
			'[data-target="carousel_slider"]'
		);
		if (!carouselSlider) {
			console.warn("Carousel elements not ready");
			return;
		}

		// Fade in the slider arrows immediately
		const sliderArrows = document.querySelector('[data-name="slider-arrows"]');
		if (sliderArrows) {
			gsap.to(sliderArrows, {
				opacity: 1,
				duration: 0.4, // Reduced duration for faster fade in
				ease: "power2.out",
			});
		}

		// Initialize the carousel slider with faster transitions
		studentsSwiper = new Swiper(carouselSlider, {
			modules: [Navigation, Pagination, EffectFade],
			slidesPerView: 1,
			spaceBetween: 0,
			speed: 300, // Reduced from 500ms to 300ms for faster transitions
			effect: "fade",
			loop: true,
			allowTouchMove: true,
			allowMouseEvents: true,
			fadeEffect: {
				crossFade: true,
			},
			navigation: {
				nextEl: '[data-target="carousel_slider"] .swiper-button-next',
				prevEl: '[data-target="carousel_slider"] .swiper-button-prev',
			},
			pagination: {
				el: '[data-target="carousel_slider"] .swiper-pagination',
				clickable: true,
				type: "bullets",
				renderBullet: function (index, className) {
					return (
						'<span class="' +
						className +
						'">' +
						"<i></i>" +
						"<b></b>" +
						"</span>"
					);
				},
			},
			on: {
				init: function () {
					// Initialize slides
					this.slides.forEach((slide) => {
						const isActive = slide.classList.contains("swiper-slide-active");
						gsap.set(slide, { autoAlpha: isActive ? 1 : 0 });

						const illustration = slide.querySelector(".illustration");
						const illustrationBg = slide.querySelector(".illustration-bg");
						const content = slide.querySelector(
							'[data-name="carousel_item_content"]'
						);
						const fromTitle = slide.querySelector('[data-name="from_title"]');

						if (illustration && content) {
							gsap.set(illustration, { autoAlpha: 1 });
							gsap.set(content, {
								autoAlpha: window.innerWidth > 992 ? 0 : 0.2,
							});

							if (illustrationBg) {
								gsap.set(illustrationBg, { opacity: 0 });
							}

							if (fromTitle) {
								gsap.set(fromTitle, { autoAlpha: isActive ? 1 : 0 });
							}
						}
					});

					// Initialize slider counter
					updateSliderCounter(this);
				},
				slideChangeTransitionStart: function () {
					const previousIndex = this.previousIndex;
					const currentIndex = this.activeIndex;

					if (previousIndex !== undefined) {
						transitionSlides(this, previousIndex, currentIndex);
					}
				},
				slideChangeTransitionEnd: function () {
					updateSliderCounter(this);
				},
			},
		});

		// Start animation for first slide
		const currentSlide = studentsSwiper.slides[studentsSwiper.activeIndex];
		const illustrationBg = currentSlide.querySelector(".illustration-bg");

		if (illustrationBg) {
			gsap.to(illustrationBg, {
				opacity: 1,
				duration: 0.8,
				ease: "power2.out",
				onComplete: () => {
					if (typeof animateSlideContent === "function") {
						animateSlideContent(studentsSwiper);
					}
				},
			});
		} else {
			if (typeof animateSlideContent === "function") {
				animateSlideContent(studentsSwiper);
			}
		}
	};

	// Animation functions for slides
	function animateSlideContent(swiper) {
		const currentSlide = swiper.slides[swiper.activeIndex];
		const illustration = currentSlide.querySelector(".illustration");
		const content = currentSlide.querySelector(
			'[data-name="carousel_item_content"]'
		);
		const fromTitle = currentSlide.querySelector('[data-name="from_title"]');

		if (!illustration || !content) return;

		const slideTimeline = gsap.timeline({ delay: 1 });

		slideTimeline
			.to(illustration, {
				autoAlpha: 0,
				duration: 1.2,
				ease: "power2.out",
			})
			.to(
				content,
				{
					autoAlpha: 1,
					duration: 0.8,
					ease: "power2.out",
				},
				"-=1"
			)
			.set(
				fromTitle,
				{
					autoAlpha: 1,
					opacity: 1,
				},
				0
			);
	}

	function transitionSlides(swiper, previousIndex, currentIndex) {
		const previousSlide = swiper.slides[previousIndex];
		const currentSlide = swiper.slides[currentIndex];

		gsap.set(currentSlide, { autoAlpha: 1 });
		gsap.set(currentSlide.querySelector(".illustration"), { autoAlpha: 1 });
		gsap.set(
			currentSlide.querySelector('[data-name="carousel_item_content"]'),
			{
				autoAlpha: window.innerWidth > 992 ? 0 : 0.2,
			}
		);

		const tl = gsap.timeline({
			onComplete: () => {
				animateSlideContent(swiper);
			},
		});

		tl.to(previousSlide, {
			autoAlpha: 0,
			duration: 0.5,
			ease: "power2.out",
		});
	}

	function updateSliderCounter(swiper) {
		const counter = document.querySelector('[data-name="slider-pageof"]');
		if (counter) {
			const currentIndex = swiper.realIndex + 1;
			const totalSlides = swiper.slides.length;
			counter.textContent = `${currentIndex} of ${totalSlides}`;
		}
	}

	// Pin sections animation for bottom sections
	const pinnedContainer = document.querySelector(
		'[data-name="pinned_home_bottom"]'
	);
	const campusSection = document.querySelector('[data-name="campus_section"]');
	const activitiesSection = document.querySelector(
		'[data-name="activities_section"]'
	);
	const latestNewsSection = document.querySelector(
		'[data-name="latest_news_section"]'
	);
	const statsSection = document.querySelector('[data-name="stats_section"]');

	if (
		pinnedContainer &&
		campusSection &&
		activitiesSection &&
		window.innerWidth >= 992
	) {
		// Get the background images for parallax effect
		const campusImage = campusSection.querySelector(".imgparallax");
		const activitiesImage = activitiesSection.querySelector(".imgparallax");

		// Set initial positions for sections
		gsap.set(campusSection, { yPercent: 0 });
		gsap.set(activitiesSection, { yPercent: 0 });
		gsap.set(statsSection, { yPercent: 0 });

		// Set initial opacity of latest news to 0
		if (latestNewsSection) {
			gsap.set(latestNewsSection, { opacity: 0 });
		}

		// Create a ScrollTrigger for the entire pinned container
		ScrollTrigger.create({
			trigger: pinnedContainer,
			start: "top top",
			end: "+=300%",
			pin: true,
			pinSpacing: true,
			anticipatePin: 1,
		});

		// Create a master timeline for coordinated animations
		const masterTimeline = gsap.timeline({
			scrollTrigger: {
				trigger: pinnedContainer,
				start: "top top",
				end: "+=300%",
				scrub: 1,
			},
		});

		// Initialize images with offset for parallax effect
		if (campusImage) gsap.set(campusImage, { y: -(window.innerHeight / 3) });
		if (activitiesImage)
			gsap.set(activitiesImage, { y: -(window.innerHeight / 3) });

		// SEQUENTIAL ANIMATIONS

		// STAGE 1: Campus section moves up
		masterTimeline.to(
			campusSection,
			{
				yPercent: -100,
				ease: "none",
				duration: 1,
			},
			0
		);

		// Parallax effect for campus image
		if (campusImage) {
			masterTimeline.to(
				campusImage,
				{
					y: 0,
					ease: "none",
					duration: 1,
				},
				0
			);
		}

		// STAGE 2: Activities section moves up
		masterTimeline
			.to(
				activitiesSection,
				{
					yPercent: -200,
					ease: "none",
					duration: 2,
				},
				0.5
			)
			.to(
				[activitiesSection, campusSection, statsSection],
				{
					yPercent: -300,
					ease: "none",
					duration: 2,
				},
				"+=.2"
			);

		// Parallax effect for activities image
		if (activitiesImage) {
			masterTimeline.to(
				activitiesImage,
				{
					y: 0,
					ease: "none",
					duration: 2,
				},
				0.5
			);
		}

		// STAGE 3: Latest news fades in
		if (latestNewsSection) {
			masterTimeline.to(
				latestNewsSection,
				{
					opacity: 1,
					ease: "power1.inOut",
					duration: 0.5,
				},
				0.8
			);
		}
	}

	// Add click event listener for banner arrow to scroll to second grid banner home
	const bannerArrow = document.querySelector(
		'[data-target="banner_arrow_home"]'
	);
	if (bannerArrow) {
		bannerArrow.addEventListener("click", function (e) {
			e.preventDefault();
			console.log('click banner arrow home');
			
			if (lenis) {
				// Get the image_text_sections element and calculate scroll from its top
				const imageTextSections = document.querySelector('[data-name="image_text_sections"]');
				if (imageTextSections) {
					const sectionTop = imageTextSections.offsetTop;
					const targetScroll = sectionTop + (window.innerHeight * 3); // 200vh from section top
					
					lenis.scrollTo(targetScroll, {
						duration: 2,
						ease: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), 
					});
				}
			}
		});
	}

	// After all ScrollTrigger setup
	window.addEventListener("load", function () {
		// Additional safeguard - ensure we're at top after everything loads
		window.scrollTo(0, 0);
		document.documentElement.scrollTop = 0;
		document.body.scrollTop = 0;

		// If lenis exists, also scroll it to top
		if (lenis) {
			lenis.scrollTo(0, { immediate: true });
		}

		const pinnedSections = document.querySelectorAll(".pin-spacer");
		pinnedSections.forEach((section) => {
			section.style.zIndex = "auto";
		});

		// Add resize listener for mobile content height calculation
		if (window.innerWidth < 992) {
			window.addEventListener("resize", updateContentHeight);
			window.addEventListener("resize", equalizeSliderItemTitles);
		}
	});
}

export { homepage };
